import React, { useState, useEffect } from 'react';
import './Dashboard.css';
import useAnalytics from '../hooks/useAnalytics';
import useMenuCounts from '../hooks/useMenuCounts';
import useProducts from '../hooks/useProducts';
import AdminLoading from './AdminLoading';
import { adminUserService } from '../../services/adminUserService';
import { extractCreatorId, getAuthHeaders } from '../utils/authUtils';
import { getApiUrl } from '../../config/environment';
import { fetchAllAdminOrdersForAgg } from '../utils/adminOrdersBulkFetch';
import { computeCreatorRevenueTotals } from '../utils/teamSalesReport';
import { analyticsAdminService } from '../../services/api';
import { ADMIN_PANEL_BASE_PATH } from '../../constants/adminPanelRoutes';
import { hasAccess } from '../constants/teamAccessPermissions';
import {
  buildViewsMap,
  mergeProductViews,
  sumPdpViews,
} from '../utils/pdpViews';
// ✅ useWishlistAnalytics import removed - Most Liked Products section removed

const Dashboard = ({ currentUser, onNavigate }) => {
  const isTeamMember = currentUser?.role === 'team';
  const { data: rawDashboardData, loading, error, refetch } = useAnalytics();
  const { counts: menuCounts, refetch: refetchMenuCounts } = useMenuCounts();
  const { products: allProducts, refetch: refetchProducts } = useProducts();
  const [teamMemberRevenue, setTeamMemberRevenue] = useState(null);

  // For team members: filter out admin's and other team members' products/orders + override counts
  const dashboardData = React.useMemo(() => {
    if (!rawDashboardData) return rawDashboardData;
    if (!isTeamMember) {
      const loaded = (allProducts || []).length;
      const fromMenu =
        menuCounts?.totalProducts != null ? Number(menuCounts.totalProducts) : null;
      const fromAnalytics =
        rawDashboardData.totalProducts != null ? Number(rawDashboardData.totalProducts) : null;
      const totalProducts =
        fromMenu != null && fromMenu >= loaded
          ? fromMenu
          : loaded > 0
            ? loaded
            : fromAnalytics ?? 0;
      return { ...rawDashboardData, totalProducts };
    }

    const myId = String(currentUser?._id || currentUser?.id || '');
    if (!myId) return rawDashboardData;

    const isMyProduct = (product) => {
      const creatorId = extractCreatorId(product.createdBy);
      return creatorId === myId;
    };

    const isOrderWithMyProducts = (order) => {
      const items = order.items || [];
      return items.some(item => {
        const product = item.product || item.productId;
        if (!product) return false;
        if (typeof product !== 'object') return false;
        const creatorId = extractCreatorId(product.createdBy);
        return creatorId === myId;
      });
    };

    const myProductsCount = (allProducts || []).filter(isMyProduct).length;
    const menuProductTotal =
      menuCounts?.totalProducts != null ? Number(menuCounts.totalProducts) : null;
    const resolvedProductCount =
      menuProductTotal != null && menuProductTotal >= myProductsCount
        ? menuProductTotal
        : myProductsCount;

    return {
      ...rawDashboardData,
      lowStockProducts: (rawDashboardData.lowStockProducts || []).filter(isMyProduct),
      recentOrders: (rawDashboardData.recentOrders || []).filter(isOrderWithMyProducts),
      totalProducts: resolvedProductCount,
      totalOrders: menuCounts.totalOrders ?? rawDashboardData.totalOrders,
      totalRevenue: teamMemberRevenue !== null ? teamMemberRevenue.total : 0,
      todayOrders: teamMemberRevenue !== null ? teamMemberRevenue.todayOrders : 0,
      monthOrders: teamMemberRevenue !== null ? teamMemberRevenue.monthOrders : 0,
      todayRevenue: teamMemberRevenue !== null ? teamMemberRevenue.today : 0,
      monthRevenue: teamMemberRevenue !== null ? teamMemberRevenue.month : 0,
    };
  }, [rawDashboardData, isTeamMember, currentUser, menuCounts, teamMemberRevenue, allProducts]);
  // ✅ useWishlistAnalytics hook removed - Most Liked Products section removed
  const [retryCount, setRetryCount] = useState(0);
  const [recentActivity, setRecentActivity] = useState([]);
  const [activeUsersCount, setActiveUsersCount] = useState(0);
  const [wishlistSummary, setWishlistSummary] = useState({
    totalLikes: null,
    catalogProducts: null,
  });
  const [totalProductPageViews, setTotalProductPageViews] = useState(null);
  const [productViewsTop, setProductViewsTop] = useState([]);

  // Fetch orders and calculate revenue for team members (their orders only)
  const fetchTeamMemberRevenue = React.useCallback(() => {
    if (!isTeamMember || !currentUser) return;
    const myId = String(currentUser._id || currentUser.id || '');
    if (!myId) return;

    const doFetch = async () => {
      try {
        const ordersList = await fetchAllAdminOrdersForAgg();
        const totals = computeCreatorRevenueTotals(ordersList, myId);
        setTeamMemberRevenue(totals);
      } catch (err) {
        console.error('Error fetching team member revenue:', err);
      }
    };
    doFetch();
  }, [isTeamMember, currentUser]);

  useEffect(() => {
    fetchTeamMemberRevenue();
  }, [fetchTeamMemberRevenue]);

  useEffect(() => {
    if (isTeamMember) return undefined;
    let cancelled = false;
    const loadWishlist = async () => {
      try {
        const res = await fetch(getApiUrl('/admin/wishlists/analytics'), { headers: getAuthHeaders() });
        if (!res.ok || cancelled) return;
        const json = await res.json();
        const d = json?.data ?? json;
        const totalLikes =
          d.totalWishlistItems ??
          d.totalItems ??
          d.totalLikes ??
          d.totalSaves ??
          d.summary?.totalItems ??
          (typeof d.total === 'number' ? d.total : null);
        const productsCount =
          dashboardData?.totalProducts != null ? Number(dashboardData.totalProducts) : null;
        setWishlistSummary({
          totalLikes: totalLikes != null ? Number(totalLikes) : null,
          catalogProducts: productsCount,
        });
      } catch (e) {
        console.warn('Dashboard wishlist analytics:', e);
      }
    };
    loadWishlist();
    return () => {
      cancelled = true;
    };
  }, [dashboardData?.totalProducts, isTeamMember]);

  useEffect(() => {
    let cancelled = false;
    const loadViews = async () => {
      try {
        const res = await analyticsAdminService.getProductViewsSummary();
        if (cancelled) return;
        setProductViewsTop(Array.isArray(res.topProducts) ? res.topProducts : []);
        setTotalProductPageViews(res.success ? res.totalViews : null);
      } catch {
        if (!cancelled) setTotalProductPageViews(null);
      }
    };
    loadViews();
    const id = window.setInterval(loadViews, 45000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  const displayProductPageViews = React.useMemo(() => {
    if (!isTeamMember) {
      return totalProductPageViews;
    }
    const myId = String(currentUser?._id || currentUser?.id || '');
    if (!myId) return totalProductPageViews;
    const myProducts = (allProducts || []).filter(
      (p) => extractCreatorId(p.createdBy) === myId
    );
    const viewsMap = buildViewsMap(productViewsTop);
    const merged = mergeProductViews(myProducts, viewsMap);
    return sumPdpViews(merged);
  }, [
    isTeamMember,
    currentUser,
    allProducts,
    productViewsTop,
    totalProductPageViews,
  ]);

  useEffect(() => {
    loadRecentActivity();
  }, [dashboardData, activeUsersCount]);

  // Fetch active users count (admin / Users|View only)
  useEffect(() => {
    if (isTeamMember || !hasAccess('Users|View', currentUser)) return;
    const fetchActiveUsersCount = async () => {
      try {
        const response = await adminUserService.getActiveUsersCount();
        if (response.success && response.data) {
          setActiveUsersCount(response.data.count || 0);
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('Active users count unavailable:', error?.message || error);
        }
      }
    };
    fetchActiveUsersCount();
  }, [isTeamMember, currentUser]);

  const loadRecentActivity = async () => {
    try {
      // Generate recent activity based on real data
      const activity = [];
      
      if (dashboardData.recentOrders && dashboardData.recentOrders.length > 0) {
        dashboardData.recentOrders.slice(0, 5).forEach((order, index) => {
          activity.push({
            id: `order-${index}`,
            type: 'order',
            message: `Order ${order.orderNumber} ${order.status}`,
            time: new Date(order.createdAt).toLocaleString(),
            icon: 'fas fa-shopping-cart'
          });
        });
      }
      
      if (dashboardData.lowStockProducts && dashboardData.lowStockProducts.length > 0) {
        dashboardData.lowStockProducts.slice(0, 3).forEach((product, index) => {
          // Calculate total stock from the Map structure
          let totalStock = 0;
          if (product.stock && product.stock.size > 0) {
            for (const [color, sizeMap] of product.stock) {
              for (const [size, quantity] of sizeMap) {
                totalStock += quantity || 0;
              }
            }
          }
          
          activity.push({
            id: `stock-${index}`,
            type: 'product',
            message: `Low stock: ${product.name} (${totalStock} left)`,
            time: 'Recently',
            icon: 'fas fa-exclamation-triangle'
          });
        });
      }
      
      // Add some default activities if no real data
      if (activity.length === 0) {
        activity.push(
          { id: 1, type: 'stats', message: `Total Products: ${dashboardData.totalProducts || 0}`, time: 'Live Data', icon: 'fas fa-box' },
          { id: 2, type: 'stats', message: `Total Orders: ${dashboardData.totalOrders || 0}`, time: 'Live Data', icon: 'fas fa-shopping-cart' },
          { id: 3, type: 'stats', message: `Active Users: ${activeUsersCount || 0}`, time: 'Live Data', icon: 'fas fa-users' },
          { id: 4, type: 'stats', message: `Total Revenue: PKR ${(dashboardData.totalRevenue || 0).toLocaleString()}`, time: 'Live Data', icon: 'fas fa-rupee-sign' }
        );
      }
      
      setRecentActivity(activity);
      
    } catch (error) {
      console.error('Error loading recent activity:', error);
    }
  };

  const handleRefresh = () => {
    refetch();
    analyticsAdminService.getProductViewsSummary().then((res) => {
      if (res.success) {
        setTotalProductPageViews(res.totalViews);
        setProductViewsTop(Array.isArray(res.topProducts) ? res.topProducts : []);
      } else {
        setTotalProductPageViews(null);
        setProductViewsTop([]);
      }
    });
    if (isTeamMember) {
      fetchTeamMemberRevenue();
      refetchProducts();
      refetchMenuCounts();
    }
  };

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
    refetch();
  };

  const handleLoginRedirect = () => {
    window.location.href = `${ADMIN_PANEL_BASE_PATH}/login`;
  };

  // Navigation handlers for dashboard sections
  const handleProductsClick = () => {
    if (onNavigate) {
      onNavigate('products');
    }
  };

  const handleOrdersClick = () => {
    if (onNavigate) {
      onNavigate('orders');
    }
  };

  const handleUsersClick = () => {
    if (onNavigate) {
      onNavigate('users');
    }
  };

  const handleRevenueClick = () => {
    if (!onNavigate) return;
    if (isTeamMember && hasAccess('Sales|View', currentUser) && !hasAccess('Revenue & Accounts|View', currentUser)) {
      onNavigate('sales');
      return;
    }
    onNavigate('revenue');
  };

  const handleAnalyticsClick = () => {
    if (onNavigate) {
      onNavigate('analytics');
    }
  };

  const handlePdpViewsClick = () => {
    if (onNavigate) {
      onNavigate('pdp-views');
    }
  };

  const handleSalesClick = () => {
    if (onNavigate) {
      onNavigate('sales');
    }
  };

  const handleAddProductClick = () => {
    if (onNavigate) {
      onNavigate('add-product');
    }
  };

  if (loading) {
    return (
      <AdminLoading
        title="Loading Dashboard"
        subtitle="Fetching analytics from server..."
        icon="📊"
      />
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <div className="error-content">
          <div className="error-icon">⚠️</div>
          <h3>Error Loading Dashboard</h3>
          <p className="error-message">{error}</p>
          
          {error.includes('Unauthorized') || error.includes('login') ? (
            <div className="error-actions">
              <button onClick={handleLoginRedirect} className="btn-login">
                Go to Admin Login
              </button>
            </div>
          ) : (
            <div className="error-actions">
              <button onClick={handleRetry} className="btn-retry">
                Retry ({retryCount}/3)
              </button>
              {retryCount >= 3 && (
                <p className="retry-limit">Maximum retry attempts reached. Please refresh the page.</p>
              )}
            </div>
          )}
          
          <div className="error-details">
            <details>
              <summary>Technical Details</summary>
              <p>Error: {error}</p>
              <p>Time: {new Date().toLocaleString()}</p>
              <p>Retry Count: {retryCount}</p>
            </details>
          </div>
        </div>
      </div>
    );
  }

  // Safety check for dashboardData
  if (!dashboardData) {
    return (
      <AdminLoading
        title="Loading Dashboard"
        subtitle="Preparing your overview..."
        icon="📊"
      />
    );
  }

  return (
    <div className="dashboard">
      <header className="dashboard-header" aria-label="Dashboard overview">
        <div className="dashboard-header__brand">
          <span className="dashboard-header__eyebrow">
            <i className="fas fa-chart-line" aria-hidden="true" />
            {isTeamMember ? 'Your workspace' : 'Store overview'}
          </span>
          <div className="dashboard-header__titles">
            <h1>{isTeamMember ? 'Your Dashboard' : 'Dashboard'}</h1>
            <p className="dashboard-header__welcome">
              Welcome back,{' '}
              <strong>{currentUser?.name || 'Admin'}</strong>
              <span className="dashboard-header__welcome-sep" aria-hidden="true">
                {' '}
                ·{' '}
              </span>
              <span className="dashboard-header__welcome-sub">
                {isTeamMember
                  ? 'Products and orders at a glance.'
                  : "Here's your store overview."}
              </span>
            </p>
          </div>
        </div>
        <div className="dashboard-header__actions">
          <button
            type="button"
            onClick={handleRefresh}
            className="dashboard-header__refresh refresh-btn"
            title="Refresh dashboard data"
            aria-label="Refresh dashboard data"
          >
            <i className="fas fa-sync-alt dashboard-header__refresh-icon" aria-hidden="true" />
            <span className="dashboard-header__refresh-label">Refresh</span>
          </button>
        </div>
      </header>

      {/* Statistics Cards */}
      <div className="stats-grid">
        <div className="stat-card clickable" onClick={handleProductsClick} title="Click to view all products">
          <div className="stat-icon">
            <i className="fas fa-box"></i>
          </div>
          <div className="stat-content">
            <h3>Products</h3>
            <div className="stat-number">{dashboardData.totalProducts || 0}</div>
            <div className="stat-label">Total Products</div>
          </div>
          <div className="stat-arrow">
            <i className="fas fa-arrow-right"></i>
          </div>
        </div>

        <div className="stat-card clickable" onClick={handleOrdersClick} title="Click to view all orders">
          <div className="stat-icon">
            <i className="fas fa-shopping-cart"></i>
          </div>
          <div className="stat-content">
            <h3>Orders</h3>
            <div className="stat-number">{dashboardData.totalOrders || 0}</div>
            <div className="stat-label">Total Orders</div>
          </div>
          <div className="stat-arrow">
            <i className="fas fa-arrow-right"></i>
          </div>
        </div>

        <div className="stat-card clickable" onClick={handleUsersClick} title="Click to view all users">
          <div className="stat-icon">
            <i className="fas fa-users"></i>
          </div>
          <div className="stat-content">
            <h3>Users</h3>
            <div className="stat-number">{activeUsersCount || 0}</div>
            <div className="stat-label">Active Users</div>
          </div>
          <div className="stat-arrow">
            <i className="fas fa-arrow-right"></i>
          </div>
        </div>

        <div className="stat-card clickable" onClick={handleRevenueClick} title="Click to view revenue details">
          <div className="stat-icon">
            <i className="fas fa-rupee-sign"></i>
          </div>
          <div className="stat-content">
            <h3>Revenue</h3>
            <div className="stat-number">PKR {(dashboardData.totalRevenue || 0).toLocaleString()}</div>
            <div className="stat-label">Total Revenue</div>
          </div>
          <div className="stat-arrow">
            <i className="fas fa-arrow-right"></i>
          </div>
        </div>

        <div
          className="stat-card clickable"
          onClick={handlePdpViewsClick}
          title="View products with PDP view counts"
        >
          <div className="stat-icon">
            <i className="fas fa-eye"></i>
          </div>
          <div className="stat-content">
            <h3>PDP views</h3>
            <div className="stat-number">
              {displayProductPageViews != null ? displayProductPageViews.toLocaleString() : '—'}
            </div>
            <div className="stat-label">
              {isTeamMember ? 'Your product page views' : 'Total product page views'}
            </div>
          </div>
          <div className="stat-arrow">
            <i className="fas fa-arrow-right"></i>
          </div>
        </div>

        <div
          className={`stat-card${isTeamMember ? '' : ' clickable'}`}
          onClick={isTeamMember ? undefined : handleAnalyticsClick}
          title={
            isTeamMember
              ? 'Wishlist saves (admin Analytics removed for team)'
              : 'Open analytics for products and wishlist detail'
          }
        >
          <div className="stat-icon">
            <i className="fas fa-heart"></i>
          </div>
          <div className="stat-content">
            <h3>Wishlist</h3>
            <div className="stat-number">
              {wishlistSummary.totalLikes != null ? wishlistSummary.totalLikes.toLocaleString() : '—'}
            </div>
            <div className="stat-label">Total wishlist saves</div>
            {wishlistSummary.catalogProducts != null && (
              <div className="stat-sublabel" style={{ marginTop: '6px', fontSize: '0.85rem', opacity: 0.9 }}>
                Products in catalog: {wishlistSummary.catalogProducts.toLocaleString()}
              </div>
            )}
          </div>
          {!isTeamMember && (
            <div className="stat-arrow">
              <i className="fas fa-arrow-right"></i>
            </div>
          )}
        </div>
      </div>

      {/* Today / month orders & revenue */}
      <div className="additional-stats">
        <div className="dashboard-mini-stats" role="group" aria-label="Orders and revenue summary">
          <button
            type="button"
            className="dashboard-mini-stat dashboard-mini-stat--today-orders"
            onClick={handleOrdersClick}
            title="View orders"
          >
            <span className="dashboard-mini-stat__icon" aria-hidden="true">
              <i className="fas fa-calendar-day" />
            </span>
            <span className="dashboard-mini-stat__body">
              <span className="dashboard-mini-stat__label">Today&apos;s orders</span>
              <span className="dashboard-mini-stat__value">{dashboardData.todayOrders || 0}</span>
            </span>
            <i className="fas fa-chevron-right dashboard-mini-stat__chev" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="dashboard-mini-stat dashboard-mini-stat--month-orders"
            onClick={handleOrdersClick}
            title="View orders"
          >
            <span className="dashboard-mini-stat__icon" aria-hidden="true">
              <i className="fas fa-calendar-alt" />
            </span>
            <span className="dashboard-mini-stat__body">
              <span className="dashboard-mini-stat__label">This month</span>
              <span className="dashboard-mini-stat__value">{dashboardData.monthOrders || 0}</span>
            </span>
            <i className="fas fa-chevron-right dashboard-mini-stat__chev" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="dashboard-mini-stat dashboard-mini-stat--today-revenue"
            onClick={handleRevenueClick}
            title="View revenue"
          >
            <span className="dashboard-mini-stat__icon" aria-hidden="true">
              <i className="fas fa-coins" />
            </span>
            <span className="dashboard-mini-stat__body">
              <span className="dashboard-mini-stat__label">Today&apos;s revenue</span>
              <span className="dashboard-mini-stat__value">
                PKR {(dashboardData.todayRevenue || 0).toLocaleString()}
              </span>
            </span>
            <i className="fas fa-chevron-right dashboard-mini-stat__chev" aria-hidden="true" />
          </button>
          <button
            type="button"
            className="dashboard-mini-stat dashboard-mini-stat--month-revenue"
            onClick={handleRevenueClick}
            title="View revenue"
          >
            <span className="dashboard-mini-stat__icon" aria-hidden="true">
              <i className="fas fa-chart-line" />
            </span>
            <span className="dashboard-mini-stat__body">
              <span className="dashboard-mini-stat__label">Monthly revenue</span>
              <span className="dashboard-mini-stat__value">
                PKR {(dashboardData.monthRevenue || 0).toLocaleString()}
              </span>
            </span>
            <i className="fas fa-chevron-right dashboard-mini-stat__chev" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="dashboard-left">
          {/* Recent Orders */}
          {dashboardData.recentOrders && dashboardData.recentOrders.length > 0 && (
            <div className="recent-orders-card">
              <div className="card-header">
                <h3>Recent Orders</h3>
                <button className="btn-text" onClick={handleOrdersClick}>View All</button>
              </div>
              <div className="orders-list">
                {dashboardData.recentOrders.slice(0, 5).map(order => (
                  <div key={order._id} className="order-item">
                    <div className="order-info">
                      <div className="order-number">{order.orderNumber}</div>
                      <div className="customer-name">{order.user?.name || 'Unknown Customer'}</div>
                      <div className="order-total">PKR {order.total?.toLocaleString() || '0'}</div>
                      <div className={`order-status ${order.status}`}>{order.status}</div>
                    </div>
                    <div className="order-time">
                      {new Date(order.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Low Stock Products */}
          {dashboardData.lowStockProducts && dashboardData.lowStockProducts.length > 0 && (
            <div className="low-stock-card">
              <div className="card-header">
                <h3>Low Stock Products</h3>
                <button className="btn-text" onClick={handleProductsClick}>Manage Stock</button>
              </div>
              <div className="products-list">
                {dashboardData.lowStockProducts.slice(0, 5).map(product => {
                  // Calculate total stock from the Map structure
                  let totalStock = 0;
                  if (product.stock && product.stock.size > 0) {
                    for (const [color, sizeMap] of product.stock) {
                      for (const [size, quantity] of sizeMap) {
                        totalStock += quantity || 0;
                      }
                    }
                  }
                  
                  return (
                    <div key={product._id} className="product-item">
                      <div className="product-info">
                        <div className="product-name">{product.name}</div>
                        <div className="product-sku">SKU: {product.sku}</div>
                      </div>
                      <div className="stock-info">
                        <span className={`stock-count ${totalStock < 5 ? 'critical' : 'low'}`}>
                          {totalStock} left
                        </span>
                        <span className="stock-status low">Low Stock</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="dashboard-right">
          {/* Recent Activity */}
          <div className="activity-card">
            <div className="activity-header">
              <h3>Recent Activity</h3>
              {isTeamMember ? (
                hasAccess('Sales|View', currentUser) && (
                  <button type="button" className="btn-text" onClick={handleSalesClick}>
                    View Sales
                  </button>
                )
              ) : (
                <button type="button" className="btn-text" onClick={handleAnalyticsClick}>
                  View All
                </button>
              )}
            </div>
            <div className="activity-list">
              {recentActivity.map(activity => (
                <div key={activity.id} className="activity-item">
                  <div className="activity-icon">
                    <i className={activity.icon}></i>
                  </div>
                  <div className="activity-content">
                    <p className="activity-message">{activity.message}</p>
                    <span className="activity-time">{activity.time}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="quick-actions-card">
            <h3>Quick Actions</h3>
            <div className="quick-actions">
              <button className="quick-action-btn" onClick={handleAddProductClick}>
                <i className="fas fa-plus"></i>
                <span>Add Product</span>
              </button>
              <button className="quick-action-btn" onClick={handleOrdersClick}>
                <i className="fas fa-shopping-cart"></i>
                <span>View Orders</span>
              </button>
              <button className="quick-action-btn" onClick={handleUsersClick}>
                <i className="fas fa-users"></i>
                <span>Manage Users</span>
              </button>
              {isTeamMember ? (
                hasAccess('Sales|View', currentUser) && (
                  <button type="button" className="quick-action-btn" onClick={handleSalesClick}>
                    <i className="fas fa-coins"></i>
                    <span>View Sales</span>
                  </button>
                )
              ) : (
                <button type="button" className="quick-action-btn" onClick={handleAnalyticsClick}>
                  <i className="fas fa-chart-bar"></i>
                  <span>View Analytics</span>
                </button>
              )}
            </div>
          </div>

          {/* ✅ Most Liked Products section removed as per requirement */}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;